/**
 * Expiration date utilities.
 *
 * This module provides consistent expiration date checking across the application.
 * An item is considered expired only AFTER the expiration date has passed completely.
 *
 * Example: expiration_date = 2024-01-17 means:
 *   - Valid: entire day of 2024-01-17
 *   - Expired: starting 2024-01-18
 */
import dayjs from "dayjs";

/**
 * Check if an item has expired.
 *
 * An item is considered expired only AFTER the expiration date has passed.
 * The comparison is done at the date level (ignoring time).
 *
 * @param {string|Date|dayjs.Dayjs} expirationDate - The expiration date
 * @returns {boolean} True if expired, False if still valid or no date provided
 */
export const isExpired = (expirationDate) => {
    if (!expirationDate) return false;

    const expDate = dayjs(expirationDate).startOf("day");
    const today = dayjs().startOf("day");

    // Expired only if today is AFTER the expiration date
    return today.isAfter(expDate);
};

/**
 * Check if an item is expiring soon (within threshold days).
 *
 * @param {string|Date|dayjs.Dayjs} expirationDate - The expiration date
 * @param {number} daysThreshold - Number of days to consider as "expiring soon" (default: 30)
 * @returns {boolean} True if expiring within threshold and not already expired
 */
export const isExpiringSoon = (expirationDate, daysThreshold = 30) => {
    if (!expirationDate) return false;

    const expDate = dayjs(expirationDate).startOf("day");
    const today = dayjs().startOf("day");

    // Not expiring soon if already expired
    if (today.isAfter(expDate)) return false;

    const thresholdDate = today.add(daysThreshold, "day");

    // Is within threshold period (not after threshold date)
    return expDate.isBefore(thresholdDate) || expDate.isSame(thresholdDate);
};
